import type { ComponentTemplate } from "@editor/types/component-template";
import type { MenuItem } from "@replo/design-system/components/menu/Menu";

import * as React from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import ComponentTemplateDragOverlay from "@editor/components/ComponentTemplateDragOverlay";
import { getTemplateName } from "@editor/components/editor/componentTemplates";
import ExtractFromUrlForm from "@editor/components/left-bar/ExtractFromUrlForm";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentDragType from "@editor/hooks/useCurrentDragType";
import useDynamicCSSOverflow from "@editor/hooks/useDynamicCSSOverflow";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import useSearchableTemplateLeftBarCategories from "@editor/hooks/useSearchableTemplateLeftBarCategories";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { trpc } from "@editor/utils/trpc";
import { useIntervalOptions } from "@editor/utils/use-interval-options";

import IconButton from "@replo/design-system/components/button/IconButton";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Menu } from "@replo/design-system/components/menu/Menu";
import Popover from "@replo/design-system/components/popover/Popover";
import Separator from "@replo/design-system/components/Separator";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { ImageOff, MoreVertical, Search, Trash } from "lucide-react";

import IsolatedReploComponent from "../IsolatedReploComponent";
import ExtractFontsFromUrlForm from "./sections-pane/ExtractFontsFromUrlForm";

const DESIGN_SYSTEM_TEMPLATE_NAMES = ["Button", "Text", "Stars + Text"];

const ComponentPreview = ({
  componentTemplate,
  imageSrc,
  isSaved,
}: {
  componentTemplate: ComponentTemplate;
  imageSrc?: string;
  isSaved: boolean;
}) => {
  const [imageError, setImageError] = React.useState(false);

  if (isFeatureEnabled("component-previews") && componentTemplate.template) {
    return (
      <IsolatedReploComponent
        component={componentTemplate.template}
        variant="desktop"
      />
    );
  }

  if (imageError || !imageSrc) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <ImageOff size={24} className="text-muted mb-2" />
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={getTemplateName(componentTemplate) as string}
      className={twMerge(
        isSaved
          ? "absolute inset-0 m-auto h-full w-full object-contain object-center"
          : "absolute top-0 w-full object-cover",
      )}
      style={{ pointerEvents: "none" }}
      onError={() => setImageError(true)}
    />
  );
};

const ComponentTemplateListItem: React.FC<{
  componentTemplate: ComponentTemplate;
}> = ({ componentTemplate }) => {
  const modal = useModal();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = React.useState(false);
  const { currentDragType } = useCurrentDragType();
  const isSaved = componentTemplate.scope === "store";
  const isDesignSystem =
    isFeatureEnabled("html-to-replo") &&
    isSaved &&
    DESIGN_SYSTEM_TEMPLATE_NAMES.includes(
      getTemplateName(componentTemplate) as string,
    );

  const menuItems: MenuItem[] = [
    {
      id: "deleteTemplate",
      type: "leaf" as const,
      title: "Delete",
      variant: "default" as const,
      size: "sm" as const,
      startEnhancer: <Trash size={12} />,
      onSelect: () => {
        modal.openModal({
          type: "deleteComponentTemplateConfirmationModal",
          props: {
            template: componentTemplate,
          },
        });
      },
    },
  ];
  const templateName = getTemplateName(componentTemplate) as string;
  // Note (Sebas, 2023-11-30): We add a query param to the src to prevent
  // the browser from using a previous version of the images (cached)
  // format: YYYY-MM-DD
  const imageSrc =
    isSaved || isDesignSystem
      ? componentTemplate.desktopPreviewUrl
      : `/images/templates/${componentTemplate.preview}?date=2024-07-16`;

  return (
    <Popover.Root
      isOpen={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        if (!open) {
          setIsPreviewLoaded(false);
        }
      }}
    >
      <Popover.Anchor
        asChild
        onMouseEnter={() => {
          if (!isMenuOpen && !currentDragType && isSaved) {
            setIsPopoverOpen(true);
          }
        }}
        onMouseLeave={() => {
          setIsPopoverOpen(false);
        }}
      >
        <li key={componentTemplate.id}>
          <div
            className={twMerge(
              "border-0.5 border-border bg-neutral-soft group relative flex aspect-[5/4] w-full cursor-pointer flex-col justify-center gap-1 rounded-lg p-1.5",
              isSaved && "hover:bg-white",
              isMenuOpen && "bg-white",
            )}
            data-testid="component-template-draggable"
          >
            {componentTemplate.template && (
              <ComponentTemplateDragOverlay
                dragType="newComponent"
                template={componentTemplate}
                identifier={componentTemplate.id}
                scope="left-bar"
                showOutlineOnHover={false}
              />
            )}
            {isSaved && !isDesignSystem && (
              <div className="relative flex flex-shrink-0 items-start justify-between">
                <Tag size="sm" variant="info">
                  Saved
                </Tag>
                <div
                  className={twMerge(
                    "invisible relative group-hover:visible",
                    isMenuOpen && "visible",
                  )}
                >
                  <Menu
                    isOpen={isMenuOpen}
                    onRequestOpen={() => setIsMenuOpen(true)}
                    onRequestClose={() => setIsMenuOpen(false)}
                    align="start"
                    size="sm"
                    side="right"
                    disableTriggerFocusOnClose
                    items={menuItems}
                    contentClassNames="w-40"
                    trigger={
                      <IconButton
                        variant="tertiary"
                        size="sm"
                        icon={<MoreVertical size={12} />}
                        UNSAFE_className="absolute top-0 right-0 z-[2147483649]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMenuOpen(true);
                        }}
                      />
                    }
                  />
                </div>
              </div>
            )}
            <div className="relative flex-grow overflow-hidden">
              <ComponentPreview
                componentTemplate={componentTemplate}
                imageSrc={imageSrc}
                isSaved={isSaved}
              />
            </div>

            <div className="text-muted flex-shrink-0 truncate text-center text-xs">
              {templateName}
            </div>
          </div>
        </li>
      </Popover.Anchor>
      <Popover.Content
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton
        side="right"
        align="start"
        sideOffset={12}
        className={twMerge(
          "pointer-events-none isolation-auto flex w-[250px] flex-col gap-1 rounded-md opacity-0",
          isPreviewLoaded && "opacity-100",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="header-h5 w-full break-all">{templateName}</span>
          {isSaved && !isDesignSystem && (
            <Tag size="sm" variant="info">
              Saved
            </Tag>
          )}
        </div>
        <img
          src={imageSrc}
          alt={templateName}
          className="max-h-[300px] rounded object-contain"
          onLoad={() => setIsPreviewLoaded(true)}
        />
      </Popover.Content>
    </Popover.Root>
  );
};

const ComponentTemplatesList = ({
  searchString,
}: {
  searchString?: string;
}) => {
  const { project } = useCurrentProjectContext();
  const { data: savedComponentTemplatesResult, isLoading } =
    trpc.componentTemplates.find.useQuery({
      searchText: searchString,
      includeComponent: true,
      selectedCategories: [],
      scope: "store",
      componentCategoryType: ["component"],
      pageSize: "ALL",
      storeId: project?.id,
    });
  const savedComponentTemplates =
    savedComponentTemplatesResult?.componentTemplates;

  const leftBarCategories = useSearchableTemplateLeftBarCategories(
    "left-bar",
    searchString ?? "",
  );
  const baseComponentTemplates = leftBarCategories.categories.flatMap(
    (category) =>
      category.subCategories.flatMap(
        (subCategory) => subCategory.componentTemplates,
      ),
  );

  if (isLoading) {
    return (
      <ul className="h-full">
        <div className="grid grid-cols-2 gap-1.5 pb-3">
          {new Array(6).fill(0).map((_, itemIndex) => (
            <li key={itemIndex}>
              <div className="relative aspect-[16/9] w-full">
                <Skeleton className="border-border h-full w-full rounded-lg border-[0.5px]" />
              </div>
            </li>
          ))}
        </div>
      </ul>
    );
  }

  let combinedComponentTemplates: ComponentTemplate[] = [];
  let designSystemTemplates: ComponentTemplate[] = [];
  if (isFeatureEnabled("html-to-replo") && savedComponentTemplates?.length) {
    designSystemTemplates = DESIGN_SYSTEM_TEMPLATE_NAMES.map((name) =>
      savedComponentTemplates.find(
        (template) => getTemplateName(template) === name,
      ),
    ).filter(
      (template): template is ComponentTemplate => template !== undefined,
    );
    const designSystemTemplateNames = new Set(
      designSystemTemplates.map((template) => template.name),
    );
    const filteredSavedComponentTemplates = savedComponentTemplates.filter(
      (template) => !designSystemTemplateNames.has(template.name),
    );
    const filteredBaseComponentTemplates = baseComponentTemplates.filter(
      (baseTemplate) =>
        !designSystemTemplateNames.has(
          getTemplateName(baseTemplate) ?? baseTemplate.name,
        ),
    );
    combinedComponentTemplates = [
      ...filteredSavedComponentTemplates,
      ...designSystemTemplates,
      ...filteredBaseComponentTemplates,
    ];
  } else {
    combinedComponentTemplates = [
      ...(savedComponentTemplates || []),
      ...baseComponentTemplates,
    ];
  }

  if (combinedComponentTemplates.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <EmptyState
          title="No Results"
          description={
            searchString
              ? `No results matching "${searchString}"`
              : "No results found"
          }
          icon={<Search className="icon-size-default" />}
          size="sm"
          UNSAFE_className="py-[15%]"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3">
      {isFeatureEnabled("html-to-replo") && (
        <div className="border-0.5 border-border flex w-full flex-col gap-3 rounded-lg p-3">
          <ExtractFromUrlForm />
          <ExtractFontsFromUrlForm />
        </div>
      )}
      <ul className="h-full">
        <div className="grid grid-cols-2 gap-1.5 pb-3">
          {combinedComponentTemplates.map((componentTemplate) => (
            <ComponentTemplateListItem
              key={componentTemplate.id}
              componentTemplate={componentTemplate}
            />
          ))}
        </div>
      </ul>
    </div>
  );
};

const ComponentsPane: React.FC = () => {
  const { elementRef, overflowClassname } = useDynamicCSSOverflow();
  const [searchString, setSearchString] = React.useState("");
  const placeholder = useIntervalOptions({
    options: [
      "Buy box with carousel...",
      "Product grid with images...",
      "Image carousel...",
      "Buy now button...",
      "Accordion component...",
    ],
    interval: 5000,
  });
  const logEvent = useLogAnalytics();

  return (
    <div className="flex h-full w-full flex-col items-center gap-3 p-3 pt-0">
      <div className="sticky top-0 w-full bg-white">
        <DebouncedInput
          placeholder={placeholder}
          value={searchString}
          onValueChange={setSearchString}
          name="search-input"
          onBlur={() => {
            if (searchString.length > 0) {
              logEvent("components.search", {
                searchString: searchString,
              });
            }
          }}
          autoComplete="off"
          startEnhancer={<Search className="h-3 w-3" />}
          autoFocus={true}
        />
      </div>

      <Separator />

      <div className="min-h-0 w-full flex-1 px-3">
        <div className="no-scrollbar h-full overflow-y-auto">
          <div
            className={twMerge(
              "no-scrollbar absolute inset-0 mt-[92px] flex flex-col bg-transparent p-3 pt-0",
              overflowClassname,
            )}
            ref={elementRef}
            id="components-pane-search-wrapper"
          >
            <ComponentTemplatesList searchString={searchString} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentsPane;
